import DialogLayout from 'layouts/DialogLayout';
import useAddDriver, { DriverFormData } from 'hooks/useAddDriver';
import useGetDrivers, { DriverRecord } from 'hooks/payments/useGetDrivers';
import React, { useState } from 'react';

interface FormErrors {
  firstName?: string;
  lastName?: string;
  regNo?: string;
  phoneNumber?: string;
}

const initialForm: DriverFormData = {
  firstName: '',
  lastName: '',
  regNo: '',
  phoneNumber: '',
};

const DRIVER_FIELDS: {
  label: string;
  name: keyof DriverFormData;
  placeholder: string;
  type?: string;
}[] = [
  { label: 'First Name', name: 'firstName', placeholder: 'e.g. James' },
  { label: 'Last Name', name: 'lastName', placeholder: 'e.g. Mwangi' },
  {
    label: 'Vehicle Registration',
    name: 'regNo',
    placeholder: 'e.g. KCB 123A',
  },
  {
    label: 'Phone Number',
    name: 'phoneNumber',
    placeholder: 'e.g. +254 700 000000',
    type: 'tel',
  },
];

function AddDriverForm({ onSuccess }: { onSuccess: () => void }) {
  const [formData, setFormData] = useState<DriverFormData>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});

  const { mutateAsync: addDriver, isPending: loading, error } = useAddDriver();

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim())
      newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.regNo.trim())
      newErrors.regNo = 'Registration number is required';
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^\+?[\d\s-]{7,15}$/.test(formData.phoneNumber.trim())) {
      newErrors.phoneNumber = 'Enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      await addDriver(formData);
      setFormData(initialForm);
      onSuccess();
    } catch {
      // error is surfaced below
    }
  };
  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {DRIVER_FIELDS.slice(0, 2).map((field) => (
          <div key={field.name}>
            <label
              htmlFor={field.name}
              className="block text-xs font-semibold text-[#444] uppercase tracking-wider mb-1.5"
            >
              {field.label}
            </label>
            <input
              id={field.name}
              name={field.name}
              type={field.type ?? 'text'}
              value={formData[field.name]}
              onChange={handleChange}
              placeholder={field.placeholder}
              className={`w-full px-4 py-2.5 rounded-lg border text-sm text-[#1a1a1a] placeholder-[#bbb] bg-[#fafaf8] transition-colors focus:outline-none focus:ring-2 focus:ring-[#2d5a27]/30 focus:border-[#2d5a27] ${
                errors[field.name]
                  ? 'border-red-400 bg-red-50'
                  : 'border-[#ddd] hover:border-[#bbb]'
              }`}
            />
            {errors[field.name] && (
              <p className="mt-1 text-xs text-red-500">{errors[field.name]}</p>
            )}
          </div>
        ))}
      </div>

      {DRIVER_FIELDS.slice(2).map((field) => (
        <div key={field.name}>
          <label
            htmlFor={field.name}
            className="block text-xs font-semibold text-[#444] uppercase tracking-wider mb-1.5"
          >
            {field.label}
          </label>
          <input
            id={field.name}
            name={field.name}
            type={field.type ?? 'text'}
            value={formData[field.name]}
            onChange={handleChange}
            placeholder={field.placeholder}
            className={`w-full px-4 py-2.5 rounded-lg border text-sm text-[#1a1a1a] placeholder-[#bbb] bg-[#fafaf8] transition-colors focus:outline-none focus:ring-2 focus:ring-[#2d5a27]/30 focus:border-[#2d5a27] ${
              errors[field.name]
                ? 'border-red-400 bg-red-50'
                : 'border-[#ddd] hover:border-[#bbb]'
            }`}
          />
          {errors[field.name] && (
            <p className="mt-1 text-xs text-red-500">{errors[field.name]}</p>
          )}
        </div>
      ))}

      <div className="border-t border-[#ece8e0] pt-5 space-y-3">
        {error && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">
            <span>
              {error?.message ?? 'Failed to register driver. Please try again.'}
            </span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-[#2d5a27] hover:bg-[#234820] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm tracking-wide py-3 rounded-lg transition-colors"
        >
          {loading ? (
            <>
              <svg
                className="w-4 h-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
              Registering…
            </>
          ) : (
            <>
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4.5v15m7.5-7.5h-15"
                />
              </svg>
              Register Driver
            </>
          )}
        </button>
      </div>
    </form>
  );
}

function cell(value: string | number | undefined) {
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
}

const DriversTable = ({
  drivers,
  isLoading,
}: {
  drivers: DriverRecord[];
  isLoading: boolean;
}) => {
  if (isLoading) {
    return (
      <div className="rounded-xl border border-[#e5ddd0] bg-white p-12 flex justify-center">
        <div className="flex items-center gap-3 text-[#666] text-sm">
          <svg
            className="w-5 h-5 animate-spin text-[#2d5a27]"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
          Loading drivers…
        </div>
      </div>
    );
  }

  if (drivers.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-[#d4cbb8] bg-[#fafaf8] p-12 text-center text-sm text-[#666]">
        No drivers registered yet. Use <strong>Add driver</strong> to add the
        first one.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#e5ddd0] bg-white overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-[#2d5a27] text-white">
            <tr>
              <th className="px-4 py-3 font-semibold tracking-wide">#</th>
              <th className="px-4 py-3 font-semibold tracking-wide">
                First name
              </th>
              <th className="px-4 py-3 font-semibold tracking-wide">
                Last name
              </th>
              <th className="px-4 py-3 font-semibold tracking-wide">
                Registration
              </th>
              <th className="px-4 py-3 font-semibold tracking-wide">Phone</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#ece8e0]">
            {drivers.map((row, index) => (
              <tr
                key={
                  row.id ??
                  `${row.vehicleRegistrationNumber}-${row.phoneNumber}-${index}`
                }
                className="hover:bg-[#fafaf8] transition-colors"
              >
                <td className="px-4 py-3 text-[#888]">{index + 1}</td>
                <td className="px-4 py-3 text-[#1a1a1a]">{cell(row.name)}</td>
                <td className="px-4 py-3 text-[#1a1a1a]">
                  {cell(row.lastName)}
                </td>
                <td className="px-4 py-3 text-[#1a1a1a] font-medium">
                  {cell(row.vehicleRegistrationNumber)}
                </td>
                <td className="px-4 py-3 text-[#1a1a1a]">
                  {cell(row.phoneNumber)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AddDriver = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const {
    data: drivers = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useGetDrivers();
  console.log('drivers', drivers);
  return (
    <div className="min-h-screen bg-[#f5f0e8] font-sans">
      <div className="h-1 w-full bg-gradient-to-r from-[#2d5a27] via-[#c8860a] to-[#2d5a27]" />

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-full bg-[#2d5a27] flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <span className="text-xs font-semibold tracking-widest text-[#2d5a27] uppercase">
                Maasai Mara Gate Registration
              </span>
            </div>
            <h1 className="text-3xl font-bold text-[#1a1a1a] tracking-tight">
              Registered drivers
            </h1>
            <p className="mt-1 text-sm text-[#666] leading-relaxed max-w-xl">
              View drivers registered at the gate. Add new drivers to receive
              speed violation notifications.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center justify-center gap-2 shrink-0 bg-[#2d5a27] hover:bg-[#234820] text-white font-semibold text-sm tracking-wide px-5 py-3 rounded-lg transition-colors shadow-sm"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4.5v15m7.5-7.5h-15"
              />
            </svg>
            Add driver
          </button>
        </div>

        {isError && (
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3 rounded-lg">
            <span>
              {error?.message ?? 'Could not load drivers.'}{' '}
              <button
                type="button"
                onClick={() => refetch()}
                className="underline font-medium hover:no-underline"
              >
                Retry
              </button>
            </span>
          </div>
        )}

        <DriversTable drivers={drivers} isLoading={isLoading} />

        <p className="mt-6 text-center text-xs text-[#999]">
          Registered drivers will receive SMS alerts upon speed violations
          detected within the reserve.
        </p>
      </div>

      <DialogLayout
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        width="100%"
        maxWidth="28rem"
        height="auto"
      >
        <div className="mb-4 flex items-center gap-3 rounded-lg bg-[#2d5a27] px-4 py-3">
          <svg
            className="w-5 h-5 shrink-0 text-[#a8d5a2]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
            />
          </svg>
          <span className="text-white font-semibold text-sm tracking-wide">
            Register driver
          </span>
        </div>
        <AddDriverForm onSuccess={() => setModalOpen(false)} />
      </DialogLayout>
    </div>
  );
};

export default AddDriver;
